import styled, { css } from "styled-components";
import tw from "twin.macro";

import { ILinkProps } from "./Link";
import { BoxSizingStyle } from "../BoxSizingStyle";
import { FontStyle } from "../FontStyle";

interface StyledLinkProps extends ILinkProps {
  $iconOnly: boolean;
}

export const StyledLinkElement = styled.div``;

export const StyledLink = styled.a<StyledLinkProps>`
  ${FontStyle}
  ${BoxSizingStyle}
  ${tw`
    tw-antialiased
    tw-font-medium
    tw-leading-none
    tw-m-0
    tw-no-underline
    tw-text-sm
    tw-select-none
    tw-cursor-pointer
    tw-inline-flex
    tw-items-center
    tw-justify-center`}

  border: none;
  height: 32px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease, color 0.3s ease,
    border-color 0.3s ease;

  svg {
    fill: currentColor;
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    text-decoration: none;
  }

  ${(props) =>
    props.disabled &&
    css`
      cursor: default;
      opacity: 50%;
      pointer-events: none;
    `}

  ${(props) =>
    props.size === "small" &&
    css`
      height: 24px;
    `}

  ${(props) =>
    props.size === "big" &&
    css`
      height: 40px;
    `}

  ${(props) =>
    (props.variant === "primary" || props.variant === "contained") &&
    css`
      padding: 0 16px;
      background-color: var(--color-primary);
      border-radius: 16px;
      color: var(--color-theme-100);

      &:focus,
      &:hover {
        box-shadow: 0 3px 6px -2px var(--color-primary-500);
        background-color: var(--color-primary-800);
      }

      &:active {
        box-shadow: 0 4px 10px -2px var(--color-primary-600);
        background-color: var(--color-primary-800);
      }
    `}

  ${(props) =>
    (props.variant === "secondary" || props.variant === "outlined") &&
    css`
      padding: 0 16px;
      background-color: transparent;
      border-radius: 16px;
      border: solid 1px var(--color-theme-600);
      color: var(--color-theme-600);

      &:focus,
      &:hover {
        border-color: var(--color-primary);
        color: var(--color-primary);
      }

      &:active {
        border-color: var(--color-primary);
        color: var(--color-primary);
        background-color: var(--color-primary-200);
      }
    `}

  ${(props) =>
    (props.variant === "tertiary" || props.variant === "text colored") &&
    css`
      padding: 0 8px;
      background-color: transparent;
      border-radius: 6px;
      color: var(--color-primary);

      &:focus,
      &:hover {
        background-color: var(--color-primary-200);
      }

      &:active {
        background-color: var(--color-primary-300);
      }
    `}

   ${(props) =>
    (props.variant === "option" || props.variant === "text gray") &&
    css`
      padding: 0 8px;
      background-color: transparent;
      border-radius: 6px;
      color: var(--color-theme-600);

      &:focus,
      &:hover {
        background-color: var(--color-theme-300);
        color: var(--color-theme-900);
      }

      &:active {
        background-color: var(--color-theme-400);
        color: var(--color-theme-900);
      }
    `}

   ${(props) =>
    props.variant === "dark transparent" &&
    css`
      padding: 0 8px;
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: var(--ac-br-6);
      color: var(--only-white);

      &:hover {
        background-color: rgba(0, 0, 0, 0.85);
      }

      &:active {
        background-color: var(--only-black);
      }
    `}

  ${(props) =>
    props.variant === "circle raised" &&
    css`
      padding: 0 6px;
      background-color: transparent;
      border-radius: var(--ac-br-rounded);
      color: var(--color-theme-700);
      transition: ease 0.3s all;

      &:hover {
        background-color: var(--page-paper-main);
        box-shadow: var(--shadow-tertiary);
      }

      &:active {
        background-color: var(--page-paper-main);
        box-shadow: var(--shadow-tertiary-hover);
      }
    `}

    ${(props) =>
    props.variant === "text link-primary" &&
    css`
      line-height: 14px;
      letter-spacing: 0.25px;
      color: var(--color-primary);
      text-decoration: underline var(--color-primary);
      text-underline-offset: 3px;
      height: fit-content;

      &:hover {
        text-decoration: none;
      }

      &:focus {
        text-decoration: none;
        outline: 1px solid var(--color-primary);
        outline-offset: 2px;
        border-radius: 2px;
      }
    `}

    ${(props) =>
    props.variant === "text link-gray" &&
    css`
      line-height: 14px;
      letter-spacing: 0.25px;
      color: var(--color-theme-700);
      text-decoration: underline var(--color-theme-700);
      text-underline-offset: 3px;
      height: fit-content;

      &:hover {
        text-decoration: none;
      }

      &:focus {
        text-decoration: none;
        outline: 1px solid var(--color-primary);
        outline-offset: 2px;
        border-radius: 2px;
      }
    `}


   ${(props) =>
    props.$iconOnly &&
    css`
      padding: 0;
      width: 32px;

      ${props.size === "small" &&
      css`
        radius: 16px;
        width: 24px;
      `}

      ${props.size === "big" &&
      css`
        radius: 20px;
        width: 40px;
      `}
    `}

   ${(props) =>
    (props.variant === "primary" ||
      props.variant === "contained" ||
      props.variant === "secondary" ||
      props.variant === "outlined") &&
    css`
      ${StyledLinkElement}:first-child svg {
        margin-left: -6px;
      }
      ${StyledLinkElement}:last-child svg {
        margin-right: -6px;
      }

      ${props.size === "small" &&
      css`
        radius: 16px;
      `}

      ${props.size === "big" &&
      css`
        radius: 20px;
      `}
    `}

   ${(props) =>
    (props.variant === "tertiary" ||
      props.variant === "text colored" ||
      props.variant === "option" ||
      props.variant === "text gray" ||
      props.variant === "dark transparent") &&
    css`
      ${StyledLinkElement}:first-child svg {
        margin-left: -4px;
      }
      ${StyledLinkElement}:last-child svg {
        margin-right: -4px;
      }

      ${props.size === "small" &&
      css`
        radius: 4px;
      `}

      ${props.size === "big" &&
      css`
        radius: 8px;
      `}
    `}
`;

export const StyledLinkElements = styled.div`
  display: flex;
  align-items: center;

  ${StyledLinkElement} {
    display: inline-flex;
    svg {
      margin: 0 4px;
    }
  }
`;

export const StyledBackLink = styled.a`
  ${FontStyle}
  ${BoxSizingStyle}

  ${tw`
    tw-pl-8
    tw-text-xxs
    tw-uppercase
    tw-inline-flex
    tw-align-middle
    tw-pr-4
    tw-text-theme-600
    tw-cursor-pointer
  `}

  text-decoration: none;
  border: transparent 1px solid;
  border-radius: 20px;
  height: 32px;
  font-size: 10px;
  font-weight: 500;
  display: table-cell;
  vertical-align: middle;

  &:before,
  &:after {
    content: "";
    ${tw`
      tw-border-solid
      tw-text-theme-700
      tw-inline-block
      tw-p-0
      tw-w-2
      tw-h-2
      tw-bg-no-repeat
    `}
    border-width: 0 1px 1px 0;
    position: absolute;
    left: 0;
    transition: all ease 0.3s;
  }
  &:before {
    ${tw`tw-opacity-100`}
    transform: translateX(1rem) translateY(0.1rem) rotate(135deg);
  }
  &:after {
    ${tw`tw-opacity-0`}
    transform: translateX(2rem) translateY(0.1rem) rotate(135deg);
  }
  &:hover {
    text-decoration: none;
    border-color: var(--border-primary);
    &:before {
      ${tw`tw-opacity-0`}
      transform: translateX(0rem) translateY(0.1rem) rotate(135deg);
    }
    &:after {
      ${tw`tw-opacity-100`}
      transform: translateX(1rem) translateY(0.1rem) rotate(135deg);
    }
  }
`;

StyledBackLink.displayName = "StyledBackLink";
StyledLinkElement.displayName = "StyledLinkElement";
StyledLinkElements.displayName = "StyledLinkElements";
StyledLink.displayName = "StyledLink";
